import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Progress,
  Typography,
  Badge,
  Flex,
  Tooltip,
  Modal,
  Checkbox,
  Tag,
  Tabs
} from 'antd';
import BatteryGauge from 'react-battery-gauge';
import { apiGetDevicesInformation } from '@services/ManageDevices/DeviceServices';
import { apiGetFavoriteFilters } from '@services/ManageCustomers/FavoriteFilterServices';
import { showError } from '@components/notification';
import { communityList, downloadBlobFile, formatCurrency } from '@utils/utils';
import {
  SearchOutlined,
  ClearOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { SelectCommunity } from '@page/Common/SelectCommunity';
import { Link } from 'react-router-dom';
import TabPane from 'antd/es/tabs/TabPane';
import { URL_API } from '@configs/index';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import WarningImage from '@assets/images/warning.png';
import OnOffImage from '@assets/images/on-off.png';
import NbIOTImage from '@assets/images/nb-iot.png';
import Loading from '@components/Loading';
import { usePermission } from '@contexts/Auth/UsePermission';
import FilterAdvanceDrawer from '@components/FilterAdvance';

const { Option } = Select;
const { Title, Paragraph } = Typography;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('HP');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [countDetail, setCountDetail] = useState(null);
  const [custId, setCustId] = useState(null);
  const [status, setStatus] = useState('all');
  const [filterWarning, setFilterWarning] = useState(0);
  const [filterRSRP, setFilterRSRP] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const { dashboard } = usePermission();
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});
  const [favoriteFilters, setFavoriteFilters] = useState([]);
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState(undefined);
  const [loadingFavoriteFilters, setLoadingFavoriteFilters] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = () => {
    setIsOpen(true);
  };

  const onDrawerClose = () => {
    setIsOpen(false);
  };

  const fetchFavoriteFilters = async () => {
    try {
      setLoadingFavoriteFilters(true);
      const res = await apiGetFavoriteFilters();
      const customerFilters = (res.data?.data || []).filter((f) => f.filter_type === 'customer');
      setFavoriteFilters(
        customerFilters.map((f) => ({
          label: `${f.filter_name} (${f.item_ids?.length || 0} KH)`,
          value: f.id,
          item_ids: f.item_ids
        }))
      );
    } catch (err) {
      console.error('Error fetching favorite filters:', err);
    } finally {
      setLoadingFavoriteFilters(false);
    }
  };

  const fetchData = async () => {
    try {
      // setIsFetching(true);
      const listCust =
        selectedFavoriteFilter && favoriteFilters.find((f) => f.value === selectedFavoriteFilter)
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter).item_ids?.join(',')
          : undefined;
      const res = await apiGetDevicesInformation({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        customerId: custId ?? undefined,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        status: ['online', 'offline']?.includes(status) ? status : undefined,
        warning: ['warning']?.includes(status) ? true : undefined,
        rsrp: ['RSRP_STRONG', 'RSRP_NORMAL', 'RSRP_LOW']?.includes(status) ? status : undefined,
        listCust,
        ...dataFilterAdvance
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
      setCountDetail(res.data?.meta?.countDetail);
    } catch (err) {
      showError('Không lấy được dữ liệu!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 20000);

    return () => clearInterval(interval);
  }, [search, selectedCommunity, currentPage, custId, pageSize, status, dataFilterAdvance]);

  useEffect(() => {
    fetchFavoriteFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity, status]);

  useEffect(() => {
    const handleFilterEvent = (event) => {
      const { status } = event.detail;
      setStatus(status);
      setCurrentPage(1);
    };

    window.addEventListener('filterDeviceStatus', handleFilterEvent);
    return () => window.removeEventListener('filterDeviceStatus', handleFilterEvent);
  }, []);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const WarningCell = ({ record }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    const info = JSON.parse(record['/82/0']);
    const magneticAttackStatus = info?.magneticAttackStatus; // Cảnh báo đang bị tấn công từ tính
    const antiDemolitionStatus = info?.antiDemolition; // Cảnh báo đang bị tấn công tháo rời
    const happenedMagneticAttackStatus = info?.happenedMagneticAttack; // Cảnh báo đã bị tấn công từ tính
    const happenedAntiDemolitionStatus = info?.happenedAntiDemolition; // Cảnh báo đã bị tấn công tháo rời
    // const overFlowStatus = info?.overFlowStatus; // Cảnh báo dòng chảy đang vượt ngưỡng
    // const stopStatus = info?.stopStatus; // Cảnh báo kẹt công tơ
    const denseDataDiffirenceOfTheFlowPulseNumber =
      record?.denseData_diffirenceOfTheFlowPulseNumber;
    const reverseFlows = denseDataDiffirenceOfTheFlowPulseNumber?.filter(
      (item) => item < 0
    )?.length; // Cảnh báo dòng chảy ngược

    const showModal = () => {
      setIsModalVisible(true);
    };

    const handleOk = () => {
      setIsModalVisible(false);
    };

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const highlightStyle = (checked) => ({
      fontWeight: checked ? 'bold' : 'normal'
    });

    return (
      <>
        {antiDemolitionStatus ||
        happenedAntiDemolitionStatus ||
        magneticAttackStatus ||
        happenedMagneticAttackStatus ||
        reverseFlows ? (
          <>
            <div style={{ color: 'red', fontWeight: 'bold' }}>
              <ExclamationCircleOutlined /> Có cảnh báo{' '}
              <Button type="link" onClick={showModal}>
                Xem chi tiết
              </Button>
            </div>
            <Modal
              title="Chi tiết cảnh báo"
              visible={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}>
              <Checkbox checked={magneticAttackStatus} style={highlightStyle(magneticAttackStatus)}>
                Cảnh báo đang bị tấn công từ tính
              </Checkbox>
              <br />
              <Checkbox
                checked={happenedMagneticAttackStatus}
                style={highlightStyle(happenedMagneticAttackStatus)}>
                Cảnh báo đã bị tấn công từ tính
              </Checkbox>
              <br />
              <Checkbox checked={antiDemolitionStatus} style={highlightStyle(antiDemolitionStatus)}>
                Cảnh báo đang bị tấn công tháo rời
              </Checkbox>
              <br />
              <Checkbox
                checked={happenedAntiDemolitionStatus}
                style={highlightStyle(happenedAntiDemolitionStatus)}>
                Cảnh báo đã bị tấn công tháo rời
              </Checkbox>
              <br />
              <Checkbox checked={reverseFlows} style={highlightStyle(reverseFlows)}>
                Cảnh báo dòng chảy ngược
              </Checkbox>
            </Modal>
          </>
        ) : (
          <div>-</div>
        )}
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: '/84/0',
        render: (text, record) => {
          let info = JSON.parse(record['/84/0']);
          if (!info) {
            return (
              <div style={{ color: 'red', fontWeight: 'bold' }}>
                <Badge
                  status="error"
                  text="OFFLINE"
                  style={{ color: '#ff4d4f', fontSize: '18px' }}
                  size="default "
                />
              </div>
            );
          } else {
            const isOnline =
              (Number(Date.now()) - Number(record.last_seen_at)) / 1000 <=
              Number(info?.reportPeriod) + 60;
            return (
              <div style={{ color: isOnline ? 'green' : 'red', fontWeight: 'bold' }}>
                {isOnline ? (
                  <Badge
                    status="success"
                    text="ONLINE"
                    style={{ color: '#52c41a', fontSize: '18px' }}
                    size="default "
                  />
                ) : (
                  <Badge
                    status="error"
                    text="OFFLINE"
                    style={{ color: '#ff4d4f', fontSize: '18px' }}
                    size="default "
                  />
                )}
              </div>
            );
          }
        },
        width: 120
      },
      {
        title: 'Thiết bị',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 180,
        align: 'center',
        render: (text, record) => (
          <Link to={`/device/detail/${record.id}`} style={{ color: 'blue' }}>
            {`SN:${record.serial_no}/ ${record.model}`}
          </Link>
        )
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        align: 'center',
        width: 180,
        render: (text, record) => (
          <Link to={`/device/detail/${record.id}`} style={{ color: 'blue' }}>
            {`${record.customer_id}`}
          </Link>
        )
      },
      {
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
        fixed: 'left',
        align: 'center',
        width: 180,
        render: (text, record) => (
          <Link to={`/device/detail/${record.id}`} style={{ color: 'blue' }}>
            {`${record.customer_name}`}
          </Link>
        )
      },
      {
        title: 'Nhà máy',
        dataIndex: 'branch',
        align: 'center',
        width: 120
      },
      {
        title: 'TQL',
        dataIndex: 'manage_group',
        align: 'center',
        width: 70
      },
      {
        title: 'ĐHT',
        dataIndex: 'parent_device',
        align: 'center',
        width: 70
      },
      {
        title: 'Mạng lưới',
        dataIndex: 'route',
        align: 'center',
        width: 120
      },
      {
        title: 'Cỡ ĐH',
        dataIndex: 'size',
        align: 'center',
        width: 100
      },
      {
        title: 'Lần truyền cuối',
        dataIndex: 'last_seen_at',
        align: 'center',
        width: 160,
        render: (text, record) => {
          if (!record.last_seen_at) return <div>-</div>;
          return (
            <div>
              {new Intl.DateTimeFormat('vi-VN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }).format(record.last_seen_at)}
            </div>
          );
        }
      },
      {
        title: (
          <Tooltip title="Cường độ sóng (dBm)">
            <span style={{ whiteSpace: 'nowrap' }}> Cường độ sóng (dBm)</span>
          </Tooltip>
        ),
        dataIndex: '/99/0',
        align: 'center',
        render: (text, record) => {
          let info = JSON.parse(record['/99/0']);
          if (!info) return <div>-</div>;
          let rsrp = info.rsrp / 10;
          let color = rsrp >= -100 ? 'green' : rsrp >= -110 ? '#ffc107' : 'red';
          let progress =
            rsrp >= -100
              ? (rsrp + 100) / 3 + 80
              : rsrp >= -110
              ? 3 * (rsrp + 110) + 50
              : rsrp + 160;
          return (
            <Flex gap="small" vertical>
              <Progress
                percentPosition={{ align: 'start', type: 'inner' }}
                size={[100, 20]}
                strokeColor={color}
                percent={progress}
                showInfo
                format={() => `${rsrp.toString()}`}
              />
            </Flex>
          );
        },
        width: 160
      },
      // {
      //   title: (
      //     <Tooltip title="Tỷ lệ tín/tạp (dB)">
      //       <span
      //         style={{
      //           whiteSpace: 'nowrap',
      //           overflow: 'hidden',
      //           textOverflow: 'ellipsis',
      //           maxWidth: 100,
      //           display: 'inline-block'
      //         }}>
      //         Tỷ lệ tín/tạp (dB)
      //       </span>
      //     </Tooltip>
      //   ),
      //   dataIndex: '/99/0',
      //   render: (text, record) => {
      //     let info = JSON.parse(record['/99/0']);
      //     if (!info) return <div>-</div>;
      //     let snr = (info.snr / 10).toFixed(2);
      //     return <div>{snr}</div>;
      //   },
      //   width: 100
      // },
      {
        title: 'Pin (%)',
        dataIndex: 'voltage',
        align: 'center',
        render: (text, record) => {
          if (!record.voltage) return <div>-</div>;
          const installTime = new Date(record.created_at).getTime();
          const lastSeenAt = record.last_seen_at;
          const timeDifference = lastSeenAt - installTime;
          let remainingBatCap =
            ((8500 * 1000 -
              (timeDifference * 30) / (3600 * 1000) -
              record.number_of_updates * 1394.52) /
              (8500 * 1000)) *
            100;
          if (isNaN(remainingBatCap)) remainingBatCap = 0;
          remainingBatCap = Math.max(0, Math.min(100, remainingBatCap));
          return (
            <div>
              <BatteryGauge
                value={remainingBatCap}
                size={50}
                customization={{ readingText: { fontSize: 24 } }}
              />
            </div>
          );
        },
        width: 100
      },
      {
        title: (
          <Tooltip title="Chỉ số đồng hồ (m3)">
            <span style={{ whiteSpace: 'nowrap' }}> Chỉ số đồng hồ (m3)</span>
          </Tooltip>
        ),
        dataIndex: 'meter_reading',
        align: 'center',
        render: (text, record) => <div>{formatCurrency(record.meter_reading / 1000.0, 3)}</div>,
        width: 155
      }
      // {
      //   title: (
      //     <Tooltip title="Trạng thái cảnh báo">
      //       <span> Trạng thái cảnh báo </span>
      //     </Tooltip>
      //   ),
      //   dataIndex: '/82/0',
      //   align: 'center',
      //   render: (text, record) => <WarningCell record={record} />,
      //   width: 120
      // }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleCustIdChange = (e) => {
    setCustId(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      const listCust =
        selectedFavoriteFilter && favoriteFilters.find((f) => f.value === selectedFavoriteFilter)
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter).item_ids?.join(',')
          : undefined;
      await downloadBlobFile(
        `${URL_API}/api/devices/export-dashboard-report`,
        'Thống kê chung.xlsx',
        {
          keyWord: search ?? undefined,
          customerId: custId ?? undefined,
          community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
          status: status === 'all' ? undefined : status,
          warning: filterWarning == 1 ? 1 : undefined,
          rsrp: filterRSRP ?? undefined,
          listCust,
          ...dataFilterAdvance
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSearch('');
    setCustId('');
    setCurrentPage(1);
    setStatus('all');
    setFilterWarning(0);
    setFilterRSRP('');
    setDataFilterAdvance({});
    setSelectedFavoriteFilter(undefined);
    setFlagSearch(!flagSearch);
  };

  return (
    <div className="dashboard" id="device-list-table">
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]}>
          <Col xs={4} sm={3} md={2} lg={1} xl={1}>
            <Button
              icon={<FilterOutlined />}
              onClick={openDrawer}
              style={{
                backgroundColor: '#F4F5F7',
                color: '#626D7C',
                border: 'none',
                height: '36px',
                width: '100%'
              }}
            />
          </Col>
          <Col xs={20} sm={7} md={6} lg={5} xl={5}>
            <Select
              placeholder="Chọn Bộ lọc theo dõi..."
              value={selectedFavoriteFilter}
              onChange={(value) => {
                setSelectedFavoriteFilter(value);
                setCurrentPage(1);
              }}
              loading={loadingFavoriteFilters}
              allowClear
              options={favoriteFilters}
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col xs={24} sm={7} md={6} lg={5} xl={5}>
            <Input
              value={search}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm số serial, imei..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col xs={24} sm={7} md={6} lg={5} xl={5}>
            <Input
              value={custId}
              onChange={handleCustIdChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm tên, mã KH..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col xs={8} sm={5} md={3} lg={3} xl={3}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0051A9',
                borderColor: '#0051A9',
                color: '#fff',
                height: '36px',
                width: '100%'
              }}>
              Tìm kiếm
            </Button>
          </Col>
          <Col xs={8} sm={5} md={3} lg={3} xl={3}>
            <Button
              style={{
                border: 'none',
                backgroundColor: '#eceef0',
                height: '36px',
                width: '100%'
              }}
              icon={<ClearOutlined />}
              onClick={clearFilter}>
              Xoá bộ lọc
            </Button>
          </Col>
          {dashboard.export && (
            <Col xs={8} sm={6} md={2} lg={2} xl={2}>
              <Button
                color="primary"
                variant="filled"
                loading={loading}
                onClick={handleExportExcelButtonClick}
                icon={<DownloadOutlined />}
                style={{
                  backgroundColor: '#FFF',
                  borderColor: '#E8EBEF',
                  color: '#0051A9',
                  height: '36px',
                  fontWeight: '500',
                  width: '100%'
                }}>
                Xuất Excel
              </Button>
            </Col>
          )}
        </Row>
      </Card>
      {/* <Card> */}
      <Card bodyStyle={{ padding: '0px' }}>
        <Tabs
          activeKey={status}
          defaultActiveKey="all"
          onChange={(value) => {
            setCurrentPage(1);
            setStatus(value);
          }}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị Online`} key={'online'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị rớt mạng`} key={'offline'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          {/* <TabPane tab={`Thiết bị có cảnh báo`} key={'warning'}>
          <Table
            bordered
            // size="middle"
            loading={isFetching}
            dataSource={deviceList || []}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalItems,
              showSizeChanger: true,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
            }}
            style={{
              borderRadius: 0,
              thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
              // fontSize: '18px'
            }}
            scroll={{ x: 'max-content', y: 500 }}
          />
        </TabPane> */}
          <TabPane tab={`Thiết bị sóng tốt`} key={'RSRP_STRONG'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị sóng trung bình`} key={'RSRP_NORMAL'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
          <TabPane tab={`Thiết bị sóng yếu`} key={'RSRP_LOW'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 0,
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' }
                // fontSize: '18px'
              }}
              scroll={{ x: 'max-content', y: 500 }}
            />
          </TabPane>
        </Tabs>
      </Card>
      {/* </Card> */}
      <FilterAdvanceDrawer
        isOpen={isOpen}
        onDrawerClose={onDrawerClose}
        dataFilterAdvance={dataFilterAdvance}
        setDataFilterAdvance={setDataFilterAdvance}
        flagSearch={flagSearch}
        setFlagSearch={setFlagSearch}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
}
